import {
  COHORT_DIVIDER_KEY,
  NON_COHORT_KEYS,
  SELECT_ALL_COHORTS_KEY,
} from '@/components/cohorts/cohort-multi-select';
import { useCohorts } from '@/hooks/queries/cohorts';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { Icon } from '@iconify/react';
import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Selection,
  Spinner,
  Tooltip,
} from '@nextui-org/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import AnalyticsModal, {
  AnalyticsType,
} from '../../components/analytics-modal';
import DataView from '../../components/data-view/data-view';
import ExportFiltersModal from '../../components/export-filters-modal';
import {
  useExportUserAccessAnalytics,
  useGenerateSystemAccessMetricsReport,
  useGenerateUserEngagementReport,
} from '../../hooks/queries/export';
import { useGetUsers } from '../../hooks/queries/users';
import { AuthService } from '../../services/auth-service';
import {
  ActivationStatus,
  formatActivationStatus,
  formatUserCohortNames,
  formatUserVariableFillSummary,
  resolveActivationStatus,
  getUserCohortIds,
  User,
  userHasNoCohorts,
} from '../../types/user';
import BulkCohortAssignmentModal from './components/bulk-cohort-assignment-modal';
import HardDeleteUserModal from './components/hard-delete-user-modal';
import RemoveFromTenantModal from './components/remove-from-tenant-modal';
import SendInvitationModal from './components/send-invitation-modal';
import UserModal from './components/user-modal';

const SEARCH_DEBOUNCE_MS = 300;

type UserTableRow = User & {
  cohortAssignment: 'assigned' | 'unassigned';
  activationStatus: ActivationStatus;
  /** Lowercased email + names, so one search box matches all three. */
  searchText: string;
};

function getUsersFromSelection(users: User[], selection: Selection): User[] {
  if (selection === 'all') {
    return users;
  }

  const selectedIds = new Set(Array.from(selection, String));
  return users.filter((user) => selectedIds.has(String(user.id)));
}

function getSelectionCount(selection: Selection, total: number): number {
  if (selection === 'all') {
    return total;
  }

  return selection.size;
}

const UsersPage = () => {
  const [viewType, setViewType] = useState<'tile' | 'list'>('list');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const { tenantId } = useActiveTenant();
  const { data: users, isLoading, isError, refetch } = useGetUsers(tenantId);
  const { data: cohorts } = useCohorts();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [removeFromTenantUser, setRemoveFromTenantUser] = useState<User | null>(
    null
  );
  const [hardDeleteUser, setHardDeleteUser] = useState<User | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([]));
  const [isBulkCohortModalOpen, setIsBulkCohortModalOpen] = useState(false);
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [invitationUsers, setInvitationUsers] = useState<User[]>([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [analyticsType, setAnalyticsType] =
    useState<AnalyticsType>('user-engagement');
  const exportUserAccess = useExportUserAccessAnalytics();
  const generateUserEngagement = useGenerateUserEngagementReport();
  const generateSystemAccessMetrics = useGenerateSystemAccessMetricsReport();

  const handleEditUser = useCallback((user: User) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  }, []);

  const openInvitationModal = useCallback((usersToInvite: User[]) => {
    setInvitationUsers(usersToInvite);
    setIsInvitationModalOpen(true);
  }, []);

  const closeInvitationModal = () => {
    setIsInvitationModalOpen(false);
    setInvitationUsers([]);
  };

  const closeUserModal = () => {
    setIsUserModalOpen(false);
    setSelectedUser(null);
  };

  const handleCohortsSaved = () => {
    refetch();
  };

  const handleBulkImport = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      await AuthService.bulkImportUsers(file);
      toast.success(
        'Bulk import task started! You will receive an email notification once the import is complete.',
        {
          duration: 5000,
          icon: '📧',
        }
      );
    } catch (error) {
      console.error('Error starting bulk import:', error);
      toast.error('Failed to start bulk import. Please try again.');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleOpenAnalyticsModal = (type: AnalyticsType) => {
    setAnalyticsType(type);
    setIsAnalyticsModalOpen(true);
  };

  const handleGenerateAnalytics = (filters: any) => {
    if (analyticsType === 'user-engagement') {
      generateUserEngagement.mutate(filters, {
        onSuccess: () => {
          setIsAnalyticsModalOpen(false);
        },
      });
    } else if (analyticsType === 'system-access-metrics') {
      generateSystemAccessMetrics.mutate(filters, {
        onSuccess: () => {
          setIsAnalyticsModalOpen(false);
        },
      });
    }
  };

  const tableUsers = useMemo((): UserTableRow[] => {
    return (
      users?.results.map((item) => {
        const activationStatus = resolveActivationStatus(
          item.user.activation_status ?? item.activation_status
        );

        return {
          ...item.user,
          activation_status: activationStatus,
          cohortAssignment: userHasNoCohorts(item.user.cohorts)
            ? 'unassigned'
            : 'assigned',
          activationStatus,
          searchText: [
            item.user.email,
            item.user.first_name,
            item.user.last_name,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        };
      }) ?? []
    );
  }, [users]);

  const usersWithoutCohortCount = useMemo(
    () =>
      tableUsers.filter((user) => user.cohortAssignment === 'unassigned')
        .length,
    [tableUsers]
  );

  const notInvitedCount = useMemo(
    () =>
      tableUsers.filter((user) => user.activationStatus === 'not_invited')
        .length,
    [tableUsers]
  );

  const selectedUsers = useMemo(
    () => getUsersFromSelection(tableUsers, selectedKeys),
    [tableUsers, selectedKeys]
  );

  const selectedCount = getSelectionCount(selectedKeys, tableUsers.length);

  const emailColumnWidth = useMemo(() => {
    const longestEmailLength = tableUsers.reduce(
      (longest, user) => Math.max(longest, user.email?.length ?? 0),
      'Email'.length
    );

    return String(Math.min(Math.max(longestEmailLength * 7, 160), 260));
  }, [tableUsers]);

  // Column definitions (and their cell renderers) only change with the data
  // they depend on, not with selection or filter state.
  const columns = useMemo(
    () => [
      {
        uid: 'id',
        name: 'User Id',
        width: '90',
        sortable: true,
      },
      {
        uid: 'first_name',
        name: 'First name',
        width: '140',
        renderCell: (user: User) =>
          user.first_name && user.first_name !== '' ? user.first_name : 'N/A',
        sortable: true,
      },
      {
        uid: 'last_name',
        name: 'Last name',
        width: '140',
        renderCell: (user: User) =>
          user.last_name && user.last_name !== '' ? user.last_name : 'NA',
        sortable: true,
      },
      {
        uid: 'cohorts',
        name: 'Cohorts',
        wrap: true,
        width: '220',
        sortKey: 'cohortAssignment',
        renderCell: (user: UserTableRow) => {
          if (userHasNoCohorts(user.cohorts)) {
            return (
              <Tooltip content="Assign at least one cohort to this user">
                <Chip
                  size="sm"
                  variant="flat"
                  color="warning"
                  startContent={
                    <Icon
                      icon="lucide:triangle-alert"
                      className="h-3.5 w-3.5"
                    />
                  }
                >
                  No cohort
                </Chip>
              </Tooltip>
            );
          }

          return formatUserCohortNames(user.cohorts);
        },
        sortable: true,
      },
      {
        uid: 'email',
        name: 'Email',
        width: emailColumnWidth,
        renderCell: (user: User) => (
          <span className="block max-w-full truncate whitespace-nowrap">
            {user.email}
          </span>
        ),
        sortable: true,
      },
      {
        uid: 'activation_status',
        name: 'Activation Status',
        width: '150',
        sortKey: 'activationStatus',
        renderCell: (user: UserTableRow) => {
          const status = user.activationStatus;
          const color =
            status === 'activated'
              ? 'success'
              : status === 'invited'
                ? 'primary'
                : status === 'not_invited'
                  ? 'warning'
                  : 'default';

          return (
            <Chip size="sm" variant="flat" color={color}>
              {formatActivationStatus(status)}
            </Chip>
          );
        },
        sortable: true,
      },
      {
        uid: 'variables',
        name: 'Variables',
        width: '130',
        renderCell: (user: User) =>
          formatUserVariableFillSummary(user.variables),
      },
      {
        uid: 'actions',
        name: 'Actions',
        width: '160',
        align: 'end' as const,
        renderCell: (user: User) => (
          <div className="flex items-center justify-end gap-1">
            <Tooltip content="Edit">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={() => handleEditUser(user)}
              >
                <Icon icon="lucide:edit" className="w-5 h-5" />
              </Button>
            </Tooltip>
            <Tooltip content="Remove from tenant">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={() => setRemoveFromTenantUser(user)}
              >
                <Icon icon="lucide:user-minus" className="w-5 h-5" />
              </Button>
            </Tooltip>
            <Tooltip content="Send invitation">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={() => openInvitationModal([user])}
              >
                <Icon icon="lucide:mail" className="w-5 h-5" />
              </Button>
            </Tooltip>
            <Tooltip content="Hard delete user">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                color="danger"
                onPress={() => setHardDeleteUser(user)}
              >
                <Icon icon="lucide:trash-2" className="w-5 h-5" />
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    [emailColumnWidth, handleEditUser, openInvitationModal]
  );

  const tableConfig = useMemo(
    () => ({
      layout: 'fixed' as const,
      columns,
      searchable: true,
      searchKey: 'searchText',
      searchPlaceholder: 'Search by name or email...',
      searchDebounceMs: SEARCH_DEBOUNCE_MS,
      virtualization: {
        estimatedRowHeight: 44,
        maxHeight: 'calc(100vh - 260px)',
      },
      filters: [
        {
          key: 'cohorts',
          label: 'Cohorts',
          popoverPlacement: 'bottom-end' as const,
          leadingContent:
            usersWithoutCohortCount > 0 ? (
              <Chip size="sm" variant="flat" color="warning">
                {usersWithoutCohortCount} user
                {usersWithoutCohortCount === 1 ? '' : 's'} without cohort
              </Chip>
            ) : undefined,
          options: [
            {
              uid: SELECT_ALL_COHORTS_KEY,
              name: 'Select all',
              variant: 'select-all' as const,
            },
            {
              uid: COHORT_DIVIDER_KEY,
              name: '',
              variant: 'divider' as const,
            },
            { uid: 'unassigned', name: 'No cohort' },
            ...(cohorts?.results ?? []).map((cohort) => ({
              uid: String(cohort.id),
              name: cohort.name,
            })),
          ],
          selectAllOptionUids: [
            'unassigned',
            ...(cohorts?.results ?? []).map((cohort) => String(cohort.id)),
          ],
          emptySelectionShowsNone: true,
          defaultSelectAll: true,
          matchFilter: (user: UserTableRow, selectedValues: Selection) => {
            if (selectedValues === 'all') {
              return true;
            }

            const values = Array.from(selectedValues, String).filter(
              (key) => !NON_COHORT_KEYS.has(key)
            );
            const userCohortIds = getUserCohortIds(user.cohorts).map(String);
            const isUnassigned = userHasNoCohorts(user.cohorts);

            return values.some((value) => {
              if (value === 'unassigned') {
                return isUnassigned;
              }

              return userCohortIds.includes(value);
            });
          },
        },
        {
          key: 'activation_status',
          label: 'Activation Status',
          popoverPlacement: 'bottom-end' as const,
          leadingContent:
            notInvitedCount > 0 ? (
              <Chip size="sm" variant="flat" color="warning">
                {notInvitedCount} not invited
              </Chip>
            ) : undefined,
          options: [
            {
              uid: SELECT_ALL_COHORTS_KEY,
              name: 'Select all',
              variant: 'select-all' as const,
            },
            {
              uid: COHORT_DIVIDER_KEY,
              name: '',
              variant: 'divider' as const,
            },
            { uid: 'not_invited', name: 'Not Invited' },
            { uid: 'invited', name: 'Invited' },
            { uid: 'activated', name: 'Activated' },
            { uid: 'unknown', name: 'Unknown' },
          ],
          selectAllOptionUids: [
            'not_invited',
            'invited',
            'activated',
            'unknown',
          ],
          emptySelectionShowsNone: true,
          defaultSelectAll: true,
          matchFilter: (user: UserTableRow, selectedValues: Selection) => {
            if (selectedValues === 'all') {
              return true;
            }

            return Array.from(selectedValues, String)
              .filter((key) => !NON_COHORT_KEYS.has(key))
              .includes(user.activationStatus);
          },
        },
      ],
      bottomContentPlacement: 'outside',
      tdClassName: 'py-1',
      actions: {
        enableRowSelection: true,
      },
      selectionToolbar:
        selectedCount > 0 ? (
          <div className="flex w-full flex-col gap-2">
            <span className="text-sm text-default-600">
              {selectedCount} user{selectedCount === 1 ? '' : 's'} selected
            </span>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                color="primary"
                onPress={() => setIsBulkCohortModalOpen(true)}
              >
                Assign Cohorts
              </Button>
              <Button
                size="sm"
                color="primary"
                variant="flat"
                startContent={<Icon icon="lucide:mail" width="16" />}
                onPress={() => openInvitationModal(selectedUsers)}
              >
                Send Invitation
              </Button>
              <Button
                size="sm"
                variant="bordered"
                onPress={() => setSelectedKeys(new Set([]))}
              >
                Clear selection
              </Button>
            </div>
          </div>
        ) : null,
    }),
    [
      cohorts?.results,
      columns,
      notInvitedCount,
      openInvitationModal,
      selectedCount,
      selectedUsers,
      usersWithoutCohortCount,
    ]
  );

  const renderCard = (user: User) => (
    <div key={user.id} className="p-4 border rounded-lg shadow">
      <h3 className="text-lg font-semibold">{user.name}</h3>
      <p>{user.email}</p>
      <p>Role: {user.role}</p>
      <p>Status: {user.active ? 'Active' : 'Inactive'}</p>
    </div>
  );

  if (isLoading && !users)
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  if (isError)
    return (
      <div className="text-center text-danger mt-8">
        Error loading agents. Please try again later.
      </div>
    );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <DataView
        disableTileView
        data={tableUsers}
        tableConfig={tableConfig}
        viewType={viewType}
        onViewChange={setViewType}
        renderCard={renderCard}
        title="User Management"
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        actions={
          import.meta.env.VITE_ENABLE_ANALYTICS_EXPORT === 'true' && (
            <div className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleBulkImport}
                accept=".csv"
                className="hidden"
              />
              <Button
                color="primary"
                variant="flat"
                startContent={
                  <Icon icon="heroicons:document-plus" width="20" />
                }
                onClick={triggerFileInput}
                isLoading={isImporting}
              >
                Import Users
              </Button>
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    color="primary"
                    variant="flat"
                    startContent={
                      <Icon icon="heroicons:chart-bar" width="20" />
                    }
                    isLoading={
                      generateUserEngagement.isPending ||
                      generateSystemAccessMetrics.isPending
                    }
                  >
                    Analytics Reports
                  </Button>
                </DropdownTrigger>
                <DropdownMenu aria-label="Analytics Reports">
                  <DropdownItem
                    key="user-engagement"
                    startContent={<Icon icon="heroicons:users" width="18" />}
                    description="User engagement metrics based on practice creation"
                    onClick={() => handleOpenAnalyticsModal('user-engagement')}
                  >
                    User Engagement
                  </DropdownItem>
                  <DropdownItem
                    key="system-access"
                    startContent={<Icon icon="heroicons:server" width="18" />}
                    description="System-wide access and activity metrics"
                    onClick={() =>
                      handleOpenAnalyticsModal('system-access-metrics')
                    }
                  >
                    System Access Metrics
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          )
        }
      />
      <UserModal
        isOpen={isUserModalOpen}
        onClose={closeUserModal}
        user={selectedUser}
        cohortIds={getUserCohortIds(selectedUser?.cohorts)}
        onCohortsSaved={handleCohortsSaved}
      />
      <RemoveFromTenantModal
        isOpen={Boolean(removeFromTenantUser)}
        onClose={() => setRemoveFromTenantUser(null)}
        user={removeFromTenantUser}
      />
      <HardDeleteUserModal
        isOpen={Boolean(hardDeleteUser)}
        onClose={() => setHardDeleteUser(null)}
        user={hardDeleteUser}
      />
      <BulkCohortAssignmentModal
        isOpen={isBulkCohortModalOpen}
        onClose={() => setIsBulkCohortModalOpen(false)}
        selectedUsers={selectedUsers}
        onSuccess={() => {
          setSelectedKeys(new Set([]));
        }}
      />
      <SendInvitationModal
        isOpen={isInvitationModalOpen}
        onClose={closeInvitationModal}
        selectedUsers={invitationUsers}
        onSuccess={() => {
          setSelectedKeys(new Set([]));
        }}
      />
      <ExportFiltersModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={(filters) => {
          exportUserAccess.mutate(filters);
          setIsExportModalOpen(false);
        }}
        isLoading={exportUserAccess.isPending}
        title="Export User Access History"
        type="user-access"
      />
      <AnalyticsModal
        isOpen={isAnalyticsModalOpen}
        onClose={() => setIsAnalyticsModalOpen(false)}
        onGenerate={handleGenerateAnalytics}
        isLoading={
          generateUserEngagement.isPending ||
          generateSystemAccessMetrics.isPending
        }
        type={analyticsType}
      />
    </div>
  );
};

export default UsersPage;
